import { get, invoke, throttle, isEqual, noop, cloneDeep } from 'lodash';
import { actionTypes as reduxFormActionTypes, destroy } from 'redux-form';

import { isBusinessManager } from '../../business-manager-old/selectors/is-business-manager-selectors';
import { getTopology } from '../../business-manager-old/selectors/topology-selectors';
import { setIsLoading } from '../actions/set-is-loading';
import { TRIGGER_POST_PUBLISH, triggerPostPublish } from '../actions/trigger-post-publish';
import { TRIGGER_POST_DRAFT_SAVE } from '../actions/trigger-post-draft-save';
import { TRIGGER_POST_SCHEDULE } from '../actions/trigger-post-schedule';
import { TRIGGER_POST_UNSCHEDULE } from '../actions/trigger-post-unschedule';
import { publishPost } from '../actions/publish-post';
import { savePostDraft, triggerDraftLoading, SAVE_POST_DRAFT_REQUEST } from '../actions/save-post-draft';
import { showMessage } from '../actions/show-message';
import messages, {
  NOT_PUBLISHED_BLOG_POST_PUBLISH,
  POST_PUBLISH,
  POST_UPDATE,
  UNSCHEDULE,
  SCHEDULE,
} from '../components/message/messages';
import {
  PUB_SUB_CHANNEL_APP,
  PUB_SUB_CHANNEL_MODAL,
} from '@wix/communities-blog-client-common/dist/src/constants/pub-sub-channels';
import { pickDraftProps, isPublished } from '@wix/communities-blog-universal/dist/src/post-utils';
import { unschedulePost } from '../actions/unschedule-post';
import { schedulePost } from '../actions/schedule-post';
import { convertDraftContentStateToContent } from '../services/post-utils-rce';
import { isContentStateEmpty } from '../services/content-state-utils';
import hasActiveUploads from '../services/has-active-uploads';
import { POST_FORM_NAME } from '../services/post-form-settings';
import {
  CLEAN_POST_EDITOR_STATE,
  SET_POST_EDITOR_POST,
  UPDATE_POST_EDITOR_POST,
  CLOSE_POST_EDITOR_MODAL_ON_CANCEL,
  CLOSE_POST_EDITOR_MODAL_ON_PUBLISH,
  CLOSE_POST_EDITOR_MODAL_ON_BROWSER_BACK,
} from '../actions/post-editor';
import { LOCATION_CHANGE } from 'react-router-redux';
import {
  initializeMiddlewareAfterSSR,
  updateEditedPostOnDraftSaved,
  closePostEditorModalOnCancel,
  closePostEditorModalOnPublish,
  closePostEditorModalOnBrowserBack,
  logPostEditorAction,
  openPostFormEditInProgressModalForLocked,
} from '../services/post-editor';
import { STORE_CREATED } from '../actions/store-created';
import { setTimeout } from 'timers';
import { submitPostSettings } from '../actions/submit-post-settings';
import { SAVE_AND_PUBLISH_POST } from '../actions/save-and-publish-post';

const DEFAULT_THROTTLE_WAIT = 5000;
const THROTTLE_CONFIG = {
  leading: true,
  trailing: true,
};

const notPublishedBlogPublishMessage = messages[NOT_PUBLISHED_BLOG_POST_PUBLISH];
const publishMessage = messages[POST_PUBLISH];
const updateMessage = messages[POST_UPDATE];

const setIsPublishing = isPublishing => ({
  ...setIsLoading('publish', undefined, isPublishing),
  meta: { publish: [PUB_SUB_CHANNEL_APP, PUB_SUB_CHANNEL_MODAL] },
});

export default function createPostPublishMiddleware(config = {}) {
  return store => {
    const {
      throttleWait = DEFAULT_THROTTLE_WAIT,
      onPublish = noop,
      onPostEditorSet = noop,
      onSchedule = noop,
      onUnschedule = noop,
    } = config;
    let currentPromise = null;
    let currentPost = null;
    let lastSavedDraft = null;
    let currentKey = null;

    const saveDraft = throttle(
      (dispatch, { _id, draft }, key) => {
        const draftWithRawContent = convertDraftContentStateToContent(draft);
        if (
          (!currentPost._id && isEmpty(draft)) ||
          !hasChanged(draftWithRawContent, lastSavedDraft) ||
          hasActiveUploads(draft.content)
        ) {
          return;
        }
        if (currentPromise) {
          logPostEditorAction('aborting current promise');
          currentPromise.abort();
        }
        currentPromise = dispatch(savePostDraft({ _id, ...draftWithRawContent }, key));
        lastSavedDraft = draftWithRawContent;

        currentPromise
          .then(post => {
            if (key !== currentKey) {
              return;
            }

            updateEditedPostOnDraftSaved({
              previousPost: currentPost,
              post,
              dispatch,
            });
            currentPromise = null;
          })
          .catch(error => logPostEditorAction('Draft update rejected', error));
      },
      throttleWait,
      THROTTLE_CONFIG,
    );

    return next => action => {
      switch (action.type) {
        case STORE_CREATED:
          logPostEditorAction(STORE_CREATED);
          next(action);
          return initializeMiddlewareAfterSSR({ state: store.getState(), dispatch: store.dispatch });

        case SET_POST_EDITOR_POST:
          logPostEditorAction(SET_POST_EDITOR_POST, action.payload.draftKey);
          currentKey = action.payload.draftKey;
          currentPost = action.payload.post;
          lastSavedDraft = cloneDeep(get(currentPost, 'draft', null));
          // invoking next before settings current key and post will save invalid draft
          next(action);
          onPostEditorSet(store);
          return openPostFormEditInProgressModalForLocked({ state: store.getState(), dispatch: store.dispatch });

        case UPDATE_POST_EDITOR_POST:
          logPostEditorAction(UPDATE_POST_EDITOR_POST, action.payload.draftKey);
          currentKey = action.payload.draftKey;
          currentPost = { ...action.payload.post, draft: currentPost.draft };
          return next(action);

        case CLEAN_POST_EDITOR_STATE:
          logPostEditorAction(CLEAN_POST_EDITOR_STATE, get(action, 'payload.draftKey'));
          invoke(saveDraft, 'flush');
          store.dispatch(destroy(POST_FORM_NAME));
          currentKey = null;
          currentPost = {};
          lastSavedDraft = null;
          return next(action);

        case CLOSE_POST_EDITOR_MODAL_ON_CANCEL:
          next(action);
          logPostEditorAction(CLOSE_POST_EDITOR_MODAL_ON_CANCEL);
          return closePostEditorModalOnCancel({ dispatch: store.dispatch });

        case CLOSE_POST_EDITOR_MODAL_ON_PUBLISH:
          next(action);
          logPostEditorAction(CLOSE_POST_EDITOR_MODAL_ON_PUBLISH);
          return closePostEditorModalOnPublish({ dispatch: store.dispatch, post: action.payload });

        case CLOSE_POST_EDITOR_MODAL_ON_BROWSER_BACK:
          next(action);
          // allow cancel and publish actions to close the modal first
          return setTimeout(() => {
            logPostEditorAction(CLOSE_POST_EDITOR_MODAL_ON_BROWSER_BACK);
            closePostEditorModalOnBrowserBack({ state: store.getState(), dispatch: store.dispatch });
          }, 50);

        case LOCATION_CHANGE:
          logPostEditorAction(LOCATION_CHANGE, get(action, 'payload.pathname'), get(action, 'payload.action'));
          return next(action);

        case reduxFormActionTypes.CHANGE:
          if (action.form === POST_FORM_NAME) {
            logPostEditorAction(reduxFormActionTypes.CHANGE);
            currentPost = {
              ...currentPost,
              draft: {
                ...currentPost.draft,
                [action.field]: action.value,
              },
            };

            saveDraft(store.dispatch, currentPost, currentKey);
          }
          return next(action);

        case TRIGGER_POST_DRAFT_SAVE:
          logPostEditorAction(TRIGGER_POST_DRAFT_SAVE);
          invoke(saveDraft, 'flush');
          !currentPromise && store.dispatch(triggerDraftLoading());
          return next(action);

        case TRIGGER_POST_SCHEDULE: {
          logPostEditorAction(TRIGGER_POST_SCHEDULE);
          invoke(saveDraft, 'flush');

          const message = messages[SCHEDULE];

          const date = action.payload instanceof Date && action.payload.toISOString();

          Promise.resolve(currentPromise || currentPost)
            .then(post => store.dispatch(schedulePost(post._id, date)))
            .then(post => onSchedule(store, post))
            .then(() =>
              store.dispatch(
                showMessage(
                  message.content,
                  message.type,
                  message.messageKey,
                  message.linkMessageKey,
                  message.postSlug,
                  message.createVariables(date, date),
                ),
              ),
            );

          return next(action);
        }

        case TRIGGER_POST_UNSCHEDULE: {
          logPostEditorAction(TRIGGER_POST_UNSCHEDULE);
          invoke(saveDraft, 'flush');

          const message = messages[UNSCHEDULE];

          Promise.resolve(currentPromise || currentPost)
            .then(post => store.dispatch(unschedulePost(post._id)))
            .then(post => onUnschedule(store, post))
            .then(() => store.dispatch(showMessage(message.content, message.type, message.messageKey)));

          return next(action);
        }

        case TRIGGER_POST_PUBLISH: {
          logPostEditorAction(TRIGGER_POST_PUBLISH);
          invoke(saveDraft, 'flush');
          store.dispatch(setIsPublishing(true));

          let postSlug = null;

          const promise = Promise.resolve(currentPromise || currentPost)
            .then(post => store.dispatch(publishPost(post._id)))
            .then(post => {
              postSlug = post.slug;
              return onPublish(store, post);
            })
            .then(() => store.dispatch(setIsPublishing(false)))
            .catch(() => store.dispatch(setIsPublishing(false)));

          let message = isPublished(currentPost) ? updateMessage : publishMessage;
          const state = store.getState();

          const blogIsNotPublishedAndIsBM = Boolean(isBusinessManager(state) && !get(getTopology(state), 'baseUrl'));

          if (blogIsNotPublishedAndIsBM) {
            message = notPublishedBlogPublishMessage;
          }

          promise.then(() =>
            store.dispatch(
              showMessage(message.content, message.type, message.messageKey, message.linkMessageKey, postSlug),
            ),
          );
          return next(action);
        }

        case SAVE_POST_DRAFT_REQUEST:
          logPostEditorAction(SAVE_POST_DRAFT_REQUEST, get(action, 'payload.draftKey'));
          return next(action);

        case SAVE_AND_PUBLISH_POST:
          store.dispatch(submitPostSettings(action.payload));
          store.dispatch(triggerPostPublish());
          return next(action);

        default:
          return next(action);
      }
    };
  };
}

function hasChanged(a, b) {
  return !isEqual(pickDraftProps(a), pickDraftProps(b));
}

function isEmpty({ title, categoryIds, content }) {
  return !title && !get(categoryIds, 'length') && (!content || isContentStateEmpty(content));
}
